import type { SupabaseClient } from '@supabase/supabase-js';
import {
  rowToConversation,
  rowToMessage,
  rowToNotice,
  type ChatMessage,
  type ChatNotice,
  type Conversation,
  type ConversationRow,
  type ConversationStatus,
  type MessageRow,
  type NoticeRow,
} from '@lemontaigu/chat';
import type { ReceptionStore, ReviewData, SaveNoticeInput, ThreadSummary } from './receptionStore.js';

/**
 * Production `ReceptionStore` — supabase-js under the staff session (RLS gives
 * staff every thread; Realtime auth follows the auth state automatically).
 * Replies go through the staff-message edge function so the Haiku translation
 * happens server-side with the FR original preserved (ADR-0007).
 */
export class SupabaseReceptionStore implements ReceptionStore {
  constructor(
    private readonly sb: SupabaseClient,
    private readonly chatApiBase: string,
  ) {}

  async listThreads(): Promise<ThreadSummary[]> {
    const { data: convRows, error } = await this.sb
      .from('chat_conversations')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);

    // Latest message per conversation: newest N messages, first-seen wins.
    const { data: msgRows, error: msgError } = await this.sb
      .from('chat_messages')
      .select('*')
      .order('id', { ascending: false })
      .limit(500);
    if (msgError) throw new Error(msgError.message);

    const latest = new Map<string, ChatMessage>();
    for (const row of (msgRows ?? []) as MessageRow[]) {
      if (!latest.has(row.conversation_id)) latest.set(row.conversation_id, rowToMessage(row));
    }

    return ((convRows ?? []) as ConversationRow[]).map((row) => {
      const conversation = rowToConversation(row);
      return { conversation, lastMessage: latest.get(conversation.id) ?? null };
    });
  }

  async listMessages(conversationId: string): Promise<ChatMessage[]> {
    const { data, error } = await this.sb
      .from('chat_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('id', { ascending: true });
    if (error) throw new Error(error.message);
    return ((data ?? []) as MessageRow[]).map(rowToMessage);
  }

  async sendReply(conversationId: string, body: string): Promise<ChatMessage> {
    const data = await this.postChatApi<{ message: ChatMessage }>('/api/chat/staff-message', {
      conversationId,
      body,
    });
    return data.message;
  }

  /** Authenticated POST to the chat edge functions (staff access token). */
  private async postChatApi<T>(path: string, payload: unknown): Promise<T> {
    const {
      data: { session },
    } = await this.sb.auth.getSession();
    if (!session) throw new Error('session expirée — reconnectez-vous');

    const res = await fetch(`${this.chatApiBase}${path}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`envoi impossible (${res.status})`);
    return (await res.json()) as T;
  }

  async searchConversations(fromIso: string, toIso: string, room?: string): Promise<Conversation[]> {
    let query = this.sb
      .from('chat_conversations')
      .select('*')
      .gte('created_at', fromIso)
      .lte('created_at', toIso)
      .order('created_at', { ascending: false });
    if (room) query = query.eq('room', room);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return ((data ?? []) as ConversationRow[]).map(rowToConversation);
  }

  async setStatus(conversationId: string, status: ConversationStatus): Promise<void> {
    const { error } = await this.sb
      .from('chat_conversations')
      .update({ status, closed_at: status === 'closed' ? new Date().toISOString() : null })
      .eq('id', conversationId);
    if (error) throw new Error(error.message);
  }

  // ── Dynamic knowledge tier (C7) ────────────────────────────────────────────

  async listNotices(): Promise<ChatNotice[]> {
    const { data, error } = await this.sb
      .from('chat_notices')
      .select('*')
      .is('archived_at', null)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return ((data ?? []) as NoticeRow[]).map(rowToNotice);
  }

  async saveNotice(input: SaveNoticeInput): Promise<{ notice: ChatNotice; translated: boolean }> {
    return this.postChatApi<{ notice: ChatNotice; translated: boolean }>('/api/chat/notice', input);
  }

  async archiveNotice(id: string): Promise<void> {
    await this.postChatApi<{ archived: boolean }>('/api/chat/notice', { action: 'archive', id });
  }

  // ── AI feedback — « Réponse incorrecte » (C7) ──────────────────────────────

  async listFlaggedMessageIds(messageIds: number[]): Promise<number[]> {
    if (messageIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('chat_ai_feedback')
      .select('message_id')
      .in('message_id', messageIds);
    if (error) throw new Error(error.message);
    return ((data ?? []) as Array<{ message_id: number }>).map((r) => r.message_id);
  }

  async flagAiMessage(messageId: number): Promise<void> {
    const {
      data: { session },
    } = await this.sb.auth.getSession();
    if (!session) throw new Error('session expirée — reconnectez-vous');
    const { error } = await this.sb
      .from('chat_ai_feedback')
      .insert({ message_id: messageId, created_by: session.user.id });
    if (error) throw new Error(error.message);
  }

  async unflagAiMessage(messageId: number): Promise<void> {
    const { error } = await this.sb.from('chat_ai_feedback').delete().eq('message_id', messageId);
    if (error) throw new Error(error.message);
  }

  // ── Learning review queue — « À apprendre » (C9) ───────────────────────────

  async loadReviewData(fromIso: string, toIso: string): Promise<ReviewData> {
    const { data: convRows, error: convErr } = await this.sb
      .from('chat_conversations')
      .select('*')
      .gte('created_at', fromIso)
      .lte('created_at', toIso);
    if (convErr) throw new Error(convErr.message);
    const conversations = ((convRows ?? []) as ConversationRow[]).map(rowToConversation);

    // Messages for those conversations (one batched query), plus the two small
    // staff-action tables (select-all — flags and resolutions are low volume).
    const convIds = conversations.map((c) => c.id);
    let messages: ChatMessage[] = [];
    if (convIds.length > 0) {
      const { data: msgRows, error: msgErr } = await this.sb
        .from('chat_messages')
        .select('*')
        .in('conversation_id', convIds)
        .order('id', { ascending: true });
      if (msgErr) throw new Error(msgErr.message);
      messages = ((msgRows ?? []) as MessageRow[]).map(rowToMessage);
    }

    const [flagged, resolved] = await Promise.all([
      this.sb.from('chat_ai_feedback').select('message_id'),
      this.sb.from('chat_review_resolved').select('message_id'),
    ]);
    if (flagged.error) throw new Error(flagged.error.message);
    if (resolved.error) throw new Error(resolved.error.message);

    return {
      conversations,
      messages,
      flaggedIds: ((flagged.data ?? []) as Array<{ message_id: number }>).map((r) => r.message_id),
      resolvedIds: ((resolved.data ?? []) as Array<{ message_id: number }>).map((r) => r.message_id),
    };
  }

  async suggestFact(question: string, answer: string): Promise<string> {
    const data = await this.postChatApi<{ draft: string }>('/api/chat/kb-suggest', {
      question,
      answer,
    });
    return data.draft;
  }

  async resolveReviewItem(messageId: number): Promise<void> {
    const {
      data: { session },
    } = await this.sb.auth.getSession();
    if (!session) throw new Error('session expirée — reconnectez-vous');
    const { error } = await this.sb
      .from('chat_review_resolved')
      .insert({ message_id: messageId, resolved_by: session.user.id });
    if (error) throw new Error(error.message);
  }

  async unresolveReviewItem(messageId: number): Promise<void> {
    const { error } = await this.sb
      .from('chat_review_resolved')
      .delete()
      .eq('message_id', messageId);
    if (error) throw new Error(error.message);
  }

  onMessage(cb: (message: ChatMessage) => void): () => void {
    const channel = this.sb
      .channel('reception:chat_messages')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => cb(rowToMessage(payload.new as MessageRow)),
      )
      .subscribe();
    return () => {
      void channel.unsubscribe();
    };
  }
}
